/**
 * Enhanced Input Component
 * Glass input field with enhanced states
 */

import { ComponentProps, forwardRef } from 'react'
import clsx from 'clsx'

interface InputProps extends ComponentProps<'input'> {
  label?: string
  error?: string
  hint?: string
  icon?: React.ReactNode
  iconPosition?: 'left' | 'right'
}

const Input = forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  hint,
  icon,
  iconPosition = 'left',
  className = '',
  ...props
}, ref) => {
  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium mb-2 text-gray-300">
          {label}
        </label>
      )}
      
      <div className="relative">
        {icon && iconPosition === 'left' && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            {icon}
          </div>
        )}
        
        <input
          ref={ref}
          className={clsx(
            `w-full px-4 py-3 rounded-xl
            bg-white/5 backdrop-blur-md
            border transition-all duration-300
            text-white placeholder-gray-500
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-black
            disabled:opacity-50 disabled:cursor-not-allowed`,
            error
              ? 'border-red-500/50 focus:border-red-500 focus:ring-red-500/30'
              : 'border-white/10 focus:border-blue-500 focus:ring-blue-500/30 hover:border-white/20',
            icon && iconPosition === 'left' && 'pl-10',
            icon && iconPosition === 'right' && 'pr-10',
            className
          )}
          {...props}
        />
        
        {icon && iconPosition === 'right' && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
            {icon}
          </div>
        )}
      </div>
      
      {(error || hint) && (
        <p className={clsx(
          'mt-2 text-xs',
          error ? 'text-red-400' : 'text-gray-500'
        )}>
          {error || hint}
        </p>
      )}
    </div>
  )
})

Input.displayName = 'Input'

export default Input
